import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AppCrash, ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider } from './lib/auth';
import { NotificationsProvider } from './lib/notifications';
import { applyStoredTheme } from './components/layout/ThemeToggle';
import { registerServiceWorker } from './lib/offline/register';
import { persistQueries, restorePersistedQueries } from './lib/offline/persist';
import './styles/index.css';

// Set the theme before React paints, so there is no light-to-dark flash.
applyStoredTheme();

// Offline support. Production only: in dev the worker would serve a stale shell
// over Vite's HMR and turn every edit into a mystery.
registerServiceWorker();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Auth and permission errors are final — retrying just burns time.
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

// What the device kept from earlier visits goes into the cache before the first
// render, so a kept trip opens at once — and without a network, opens at all.
// IndexedDB answers in milliseconds when warm and can take a second or two on a
// cold start; the timeout is for a browser where it hangs, and a restore that
// finishes after it still lands (`restorePersistedQueries` handles the race).
await Promise.race([
  restorePersistedQueries(queryClient).catch(() => undefined),
  new Promise((resolve) => setTimeout(resolve, 3000)),
]);
persistQueries(queryClient);

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <NotificationsProvider>
            <ErrorBoundary fallback={(error) => <AppCrash error={error} />}>
              <App />
            </ErrorBoundary>
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
